import { useEffect, useState, useMemo, useCallback } from "react";
import SunCalc from "suncalc";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sun,
  Moon,
  CaretLeft,
  CaretRight,
  Crosshair,
  CalendarBlank,
  MapPin,
  WarningCircle,
  ClockCounterClockwise,
  X,
  Star,
  House,
} from "@phosphor-icons/react";
import { buildLocationLabel, buildLocationSubtitle } from "@/utils/geoLabels";
import { findStateCities } from "@/utils/usStateCities";

const STORAGE_KEY = "golden-hour-location";
const RECENTS_KEY = "golden-hour-recent-locations";
const MAX_RECENTS = 5;

// ---------- helpers ----------
const pad = (n) => String(n).padStart(2, "0");
const fmtTime = (d) =>
  d && !isNaN(d.getTime()) ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : "—";
const fmtDateInput = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtDateLong = (d, lang) =>
  d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
const compass = (azimuthRad) => {
  // SunCalc returns azimuth from south, clockwise. Convert to bearing from north.
  let deg = (azimuthRad * 180) / Math.PI + 180;
  if (deg < 0) deg += 360;
  if (deg >= 360) deg -= 360;
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const dir = dirs[Math.round(deg / 45) % 8];
  return { deg: Math.round(deg), dir };
};

// ---------- copy ----------
const COPY = {
  en: {
    title: "Golden Hour & Sun Times",
    subtitle:
      "Plan your shoot around the best natural light. Times are computed from your location and date — no internet needed once loaded.",
    location: "Location",
    auto_detect: "Use my location",
    detect_loading: "Locating...",
    detect_denied:
      "Location permission denied. Use the 'Manual' tab to enter a city.",
    detect_unavailable: "Could not detect location.",
    manual: "Manual",
    auto: "Auto",
    search_placeholder: "Search a city (e.g., New York, Madrid, Phoenix)",
    search_btn: "Search",
    no_results: "No matching city. Try a more specific name.",
    recent: "Recent",
    remove_recent: "Remove from recent",
    pin: "Pin",
    unpin: "Unpin",
    set_default: "Set as default location (loads on page open)",
    unset_default: "Unset default location",
    is_default: "Default",
    today: "Today",
    pick_date: "Pick date",
    timeline: "Daily Sun Timeline",
    sun_position: "Sun Position",
    moon_info: "Moon",
    no_location: "Set a location to see sun times for your area.",
    best_windows: "Best Shooting Windows",
    best_windows_sub:
      "The magic light. Plan your shoot inside these blocks for the most flattering, photographer-friendly light of the day.",
    window_blue_morning: "Morning Blue Hour",
    window_blue_morning_tip:
      "Cool, even light before sunrise — great for cityscapes & moody architecture.",
    window_golden_morning: "Morning Golden Hour",
    window_golden_morning_tip:
      "Warm, soft, low-angle light. The classic portrait & landscape window.",
    window_golden_evening: "Evening Golden Hour",
    window_golden_evening_tip:
      "Warmest, most flattering light of the day. Shoot wide-open for buttery bokeh.",
    window_blue_evening: "Evening Blue Hour",
    window_blue_evening_tip:
      "Famous twilight glow. Balance ambient blue with city lights for storybook scenes.",
    azimuth: "Bearing",
    sunrise: "Sunrise",
    sunset: "Sunset",
    moonrise: "Moonrise",
    moonset: "Moonset",
    phase: "Phase",
    illum: "Illumination",
    daylight: "Daylight",
    sun_alt_max: "Max sun altitude",
    tip: "Photographer's tip",
    phases: {
      nightEnd: {
        name: "Astronomical Dawn",
        sub: "Night ends",
        tip: "Sky is still inky-dark; great for star trails and Milky Way wide-fields.",
      },
      nauticalDawn: {
        name: "Nautical Dawn",
        sub: "Astronomical twilight ends",
        tip: "Horizon visible. Stars still photographable; mix with foreground.",
      },
      dawn: {
        name: "Civil Dawn / Blue Hour",
        sub: "Nautical twilight ends",
        tip: "Cool blue tones. Excellent for cityscapes and architecture before sunrise.",
      },
      sunriseEnd: {
        name: "Golden Hour (Morning)",
        sub: "Civil twilight ends",
        tip: "Warm, soft, low-angle light. Best window for portraits and landscapes.",
      },
      sunrise: {
        name: "Sunrise",
        sub: "Sun crosses horizon",
        tip: "Long shadows, warm color. Watch for lens flare; underexpose 1/3 stop to keep highlights.",
      },
      goldenHourEnd: {
        name: "Day Time Begins",
        sub: "Golden hour ends",
        tip: "Light becomes harsher. Switch to backlit subjects or seek shade.",
      },
      solarNoon: {
        name: "Solar Noon",
        sub: "Sun at peak altitude",
        tip: "Harshest light. Good for high-contrast B&W or open shade portraits.",
      },
      goldenHour: {
        name: "Golden Hour (Evening)",
        sub: "Daytime ends",
        tip: "Warmest, most flattering light of the day. Shoot wide-open for soft bokeh.",
      },
      sunsetStart: {
        name: "Sunset Begins",
        sub: "Lower limb touches horizon",
        tip: "Skies start to color. Bracket exposures (-1, 0, +1) for HDR-friendly skies.",
      },
      sunset: {
        name: "Sunset",
        sub: "Golden hour transitions",
        tip: "Set white balance to Cloudy/Shade to enhance warmth.",
      },
      dusk: {
        name: "Civil Dusk / Blue Hour",
        sub: "Civil twilight ends",
        tip: "Famous blue hour — balance ambient blue with city lights for storybook cityscapes.",
      },
      nauticalDusk: {
        name: "Nautical Dusk",
        sub: "Nautical twilight ends",
        tip: "Stars start to appear. Bridge shot between blue hour and night photography.",
      },
      night: {
        name: "Astronomical Night",
        sub: "Full darkness",
        tip: "True dark sky. Tripod + remote release. ISO 1600–6400 for Milky Way.",
      },
    },
    moon_phases: {
      new: "New Moon",
      waxing_crescent: "Waxing Crescent",
      first_quarter: "First Quarter",
      waxing_gibbous: "Waxing Gibbous",
      full: "Full Moon",
      waning_gibbous: "Waning Gibbous",
      last_quarter: "Last Quarter",
      waning_crescent: "Waning Crescent",
    },
  },
  es: {
    title: "Hora Dorada y Tiempos Solares",
    subtitle:
      "Planea tu sesion alrededor de la mejor luz natural. Los tiempos se calculan localmente — no requiere internet una vez cargado.",
    location: "Ubicacion",
    auto_detect: "Usar mi ubicacion",
    detect_loading: "Localizando...",
    detect_denied:
      "Permiso de ubicacion denegado. Usa la pestana 'Manual' para ingresar una ciudad.",
    detect_unavailable: "No se pudo detectar la ubicacion.",
    manual: "Manual",
    auto: "Automatico",
    search_placeholder: "Busca una ciudad (ej. Nueva York, Madrid, Phoenix)",
    search_btn: "Buscar",
    no_results: "Sin coincidencias. Prueba con un nombre mas especifico.",
    recent: "Recientes",
    remove_recent: "Quitar de recientes",
    pin: "Fijar",
    unpin: "Desfijar",
    set_default: "Establecer como ubicacion predeterminada (carga al abrir)",
    unset_default: "Quitar ubicacion predeterminada",
    is_default: "Predet.",
    today: "Hoy",
    pick_date: "Elegir fecha",
    timeline: "Linea de Tiempo Solar",
    sun_position: "Posicion del Sol",
    moon_info: "Luna",
    no_location: "Configura una ubicacion para ver los tiempos solares.",
    best_windows: "Mejores Ventanas para Disparar",
    best_windows_sub:
      "La luz magica. Planea tu sesion dentro de estos bloques para conseguir la luz mas favorecedora del dia.",
    window_blue_morning: "Hora Azul Matutina",
    window_blue_morning_tip:
      "Luz fria y uniforme antes del amanecer — ideal para arquitectura y paisajes urbanos.",
    window_golden_morning: "Hora Dorada Matutina",
    window_golden_morning_tip:
      "Luz calida, suave y de bajo angulo. La ventana clasica para retratos y paisajes.",
    window_golden_evening: "Hora Dorada Vespertina",
    window_golden_evening_tip:
      "La luz mas calida y favorecedora del dia. Dispara a diafragma abierto para bokeh suave.",
    window_blue_evening: "Hora Azul Vespertina",
    window_blue_evening_tip:
      "El famoso resplandor del crepusculo. Equilibra el azul ambiente con luces urbanas.",
    azimuth: "Direccion",
    sunrise: "Amanecer",
    sunset: "Atardecer",
    moonrise: "Salida luna",
    moonset: "Puesta luna",
    phase: "Fase",
    illum: "Iluminacion",
    daylight: "Horas de luz",
    sun_alt_max: "Altitud maxima",
    tip: "Consejo del fotografo",
    phases: {
      nightEnd: {
        name: "Amanecer Astronomico",
        sub: "Fin de la noche",
        tip: "Cielo aun oscuro; ideal para estelas y Via Lactea con gran angular.",
      },
      nauticalDawn: {
        name: "Amanecer Nautico",
        sub: "Fin del crepusculo astronomico",
        tip: "Horizonte visible. Estrellas aun fotografiables; combina con primer plano.",
      },
      dawn: {
        name: "Amanecer Civil / Hora Azul",
        sub: "Fin del crepusculo nautico",
        tip: "Tonos azules frios. Excelente para arquitectura antes del amanecer.",
      },
      sunriseEnd: {
        name: "Hora Dorada (Manana)",
        sub: "Fin del crepusculo civil",
        tip: "Luz calida, suave y de bajo angulo. La mejor ventana para retratos.",
      },
      sunrise: {
        name: "Amanecer",
        sub: "Sol cruza el horizonte",
        tip: "Sombras largas, color calido. Cuidado con el flare; subexpon 1/3 para conservar luces.",
      },
      goldenHourEnd: {
        name: "Comienza el Dia",
        sub: "Fin de la hora dorada",
        tip: "La luz se vuelve dura. Busca contraluz o sombra abierta.",
      },
      solarNoon: {
        name: "Mediodia Solar",
        sub: "Sol en su altitud maxima",
        tip: "Luz mas dura. Bueno para B&N de alto contraste o retratos en sombra abierta.",
      },
      goldenHour: {
        name: "Hora Dorada (Tarde)",
        sub: "Fin del dia",
        tip: "Luz mas calida y favorecedora del dia. Dispara a diafragma abierto para bokeh suave.",
      },
      sunsetStart: {
        name: "Inicia el Atardecer",
        sub: "Borde inferior toca el horizonte",
        tip: "El cielo empieza a colorearse. Hace horquillado (-1, 0, +1) para cielos HDR.",
      },
      sunset: {
        name: "Atardecer",
        sub: "Transicion de la hora dorada",
        tip: "Pon el balance de blancos en Nublado/Sombra para realzar la calidez.",
      },
      dusk: {
        name: "Crepusculo Civil / Hora Azul",
        sub: "Fin del crepusculo civil",
        tip: "La famosa hora azul — equilibra el azul ambiente con luces urbanas.",
      },
      nauticalDusk: {
        name: "Crepusculo Nautico",
        sub: "Fin del crepusculo nautico",
        tip: "Aparecen estrellas. Transicion entre la hora azul y la fotografia nocturna.",
      },
      night: {
        name: "Noche Astronomica",
        sub: "Oscuridad total",
        tip: "Cielo realmente oscuro. Tripode + disparador remoto. ISO 1600-6400 para Via Lactea.",
      },
    },
    moon_phases: {
      new: "Luna Nueva",
      waxing_crescent: "Creciente",
      first_quarter: "Cuarto Creciente",
      waxing_gibbous: "Gibosa Creciente",
      full: "Luna Llena",
      waning_gibbous: "Gibosa Menguante",
      last_quarter: "Cuarto Menguante",
      waning_crescent: "Menguante",
    },
  },
};

const DOT_COLOR = {
  nightEnd: "#1F1F3A",
  nauticalDawn: "#2D2D5A",
  dawn: "#3B82F6",
  sunriseEnd: "#F5A524",
  sunrise: "#FB923C",
  goldenHourEnd: "#FCD34D",
  solarNoon: "#FDE68A",
  goldenHour: "#F5A524",
  sunsetStart: "#FB923C",
  sunset: "#EF4444",
  dusk: "#3B82F6",
  nauticalDusk: "#2D2D5A",
  night: "#1F1F3A",
};

const PHASE_ORDER = [
  "nightEnd",
  "nauticalDawn",
  "dawn",
  "sunriseEnd",
  "sunrise",
  "goldenHourEnd",
  "solarNoon",
  "goldenHour",
  "sunsetStart",
  "sunset",
  "dusk",
  "nauticalDusk",
  "night",
];

function moonPhaseKey(p) {
  // SunCalc moon phase: 0 New, 0.25 First Quarter, 0.5 Full, 0.75 Last Quarter
  if (p < 0.03 || p > 0.97) return "new";
  if (p < 0.22) return "waxing_crescent";
  if (p < 0.28) return "first_quarter";
  if (p < 0.47) return "waxing_gibbous";
  if (p < 0.53) return "full";
  if (p < 0.72) return "waning_gibbous";
  if (p < 0.78) return "last_quarter";
  return "waning_crescent";
}

// ---------- main ----------
export default function GoldenHourPage() {
  const { lang } = useTranslation();
  const c = COPY[lang] || COPY.en;

  const [recents, setRecents] = useState(() => {
    try {
      const stored = localStorage.getItem(RECENTS_KEY);
      const arr = stored ? JSON.parse(stored) : [];
      if (!Array.isArray(arr)) return [];
      // Enforce the same pin-aware cap on hydration: keep all pinned plus
      // up to MAX_RECENTS unpinned, in the order they were last saved.
      const pinned = arr.filter((p) => p?.pinned);
      const unpinned = arr.filter((p) => !p?.pinned).slice(0, MAX_RECENTS);
      return [...pinned, ...unpinned];
    } catch {
      return [];
    }
  });

  // Initial location: prefer the user's default chip if one is set, otherwise
  // fall back to the last-picked location persisted in STORAGE_KEY.
  const [location, setLocation] = useState(() => {
    try {
      const recentRaw = localStorage.getItem(RECENTS_KEY);
      const arr = recentRaw ? JSON.parse(recentRaw) : [];
      if (Array.isArray(arr)) {
        const def = arr.find((p) => p?.isDefault);
        if (def) return { lat: def.lat, lng: def.lng, label: def.label };
      }
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [date, setDate] = useState(() => new Date());
  const [tab, setTab] = useState("auto"); // "auto" | "manual"
  const [detectState, setDetectState] = useState("idle"); // idle | loading | error
  const [detectError, setDetectError] = useState("");
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  // Persist location
  useEffect(() => {
    if (location) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
    }
  }, [location]);

  // Persist recents
  useEffect(() => {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(recents));
  }, [recents]);

  // Add (or move-to-top) a location in the recents list. Pinned chips are
  // unlimited and stay above the unpinned recents; unpinned recents are
  // capped at MAX_RECENTS so older ones drop off naturally.
  const recordRecent = useCallback((loc) => {
    if (!loc?.label) return;
    setRecents((prev) => {
      const sameKey = (p) =>
        p.label === loc.label ||
        (Math.abs(p.lat - loc.lat) < 0.001 &&
          Math.abs(p.lng - loc.lng) < 0.001);
      const existing = prev.find(sameKey);
      const filtered = prev.filter((p) => !sameKey(p));
      const next = {
        ...loc,
        pinned: existing ? !!existing.pinned : false,
      };
      const pinned = filtered.filter((p) => p.pinned);
      const unpinned = filtered.filter((p) => !p.pinned);
      if (next.pinned) {
        return [next, ...pinned, ...unpinned.slice(0, MAX_RECENTS)];
      }
      return [...pinned, next, ...unpinned.slice(0, MAX_RECENTS - 1)];
    });
  }, []);

  const removeRecent = useCallback((label) => {
    setRecents((prev) => prev.filter((p) => p.label !== label));
  }, []);

  const togglePin = useCallback((label) => {
    setRecents((prev) => {
      const updated = prev.map((p) =>
        p.label === label
          ? {
              ...p,
              pinned: !p.pinned,
              // Clearing pin also clears default — only pinned chips can be
              // the default location.
              isDefault: !p.pinned ? p.isDefault : false,
            }
          : p
      );
      const pinned = updated.filter((p) => p.pinned);
      const unpinned = updated.filter((p) => !p.pinned);
      return [...pinned, ...unpinned];
    });
  }, []);

  // Toggle the default-on-page-load flag. Only one chip can be default at a
  // time, and only pinned chips can be set as default.
  const toggleDefault = useCallback((label) => {
    setRecents((prev) =>
      prev.map((p) => {
        if (p.label === label) {
          if (!p.pinned) return p; // safety: ignore if somehow called on unpinned
          return { ...p, isDefault: !p.isDefault };
        }
        // Clear default flag on every other chip — only one default allowed.
        return p.isDefault ? { ...p, isDefault: false } : p;
      })
    );
  }, []);

  // Auto-detect on first load if no stored location
  useEffect(() => {
    if (!location && navigator.geolocation) {
      handleAutoDetect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAutoDetect = useCallback(() => {
    if (!navigator.geolocation) {
      setDetectState("error");
      setDetectError(c.detect_unavailable);
      return;
    }
    setDetectState("loading");
    setDetectError("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        // Reverse geocode for a friendly label (free, no key)
        let label = `${latitude.toFixed(3)}, ${longitude.toFixed(3)}`;
        try {
          const r = await fetch(
            `https://geocoding-api.open-meteo.com/v1/reverse?latitude=${latitude}&longitude=${longitude}&count=1&language=${lang}`
          );
          if (r.ok) {
            const j = await r.json();
            const hit = j.results?.[0];
            if (hit) label = buildLocationLabel(hit);
          }
        } catch {
          /* fall back to coords */
        }
        setLocation({ lat: latitude, lng: longitude, label });
        recordRecent({ lat: latitude, lng: longitude, label });
        setDetectState("idle");
      },
      (err) => {
        setDetectState("error");
        setDetectError(err.code === 1 ? c.detect_denied : c.detect_unavailable);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60_000 }
    );
  }, [c.detect_denied, c.detect_unavailable, lang, recordRecent]);

  const handleSearch = useCallback(
    async (e) => {
      e?.preventDefault();
      if (!search.trim()) return;
      setSearching(true);
      setSearchResults([]);

      // If the query is a US state name (or alias), prepend that state's
      // major cities so students who know the state but not a specific city
      // still get useful results.
      const stateCities = findStateCities(search);
      const isStateSearch = stateCities.length > 0;

      try {
        const r = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(
            search
          )}&count=8&language=${lang}`
        );
        const j = await r.json();
        const apiResults = j.results || [];

        // Only boost US results to the top when the user is clearly looking
        // for a US state — otherwise we'd push US cities named "Spain" or
        // "Japan" ahead of the actual countries. Open-Meteo's native order
        // (by population / relevance) is correct for non-state queries.
        if (isStateSearch) {
          apiResults.sort((a, b) => {
            const aUS = a.country_code === "US" ? 0 : 1;
            const bUS = b.country_code === "US" ? 0 : 1;
            return aUS - bUS;
          });
        }

        // De-duplicate: if the API already returned a city with the same
        // (name, admin1) we have in stateCities, drop the API copy.
        const stateKeys = new Set(
          stateCities.map((s) => `${s.name}|${s.admin1}`)
        );
        const filteredApi = apiResults.filter(
          (r) => !stateKeys.has(`${r.name}|${r.admin1}`)
        );

        setSearchResults([...stateCities, ...filteredApi].slice(0, 5));
      } catch {
        setSearchResults(stateCities.slice(0, 5));
      }
      setSearching(false);
    },
    [search, lang]
  );

  const pickResult = (r) => {
    const loc = {
      lat: r.latitude,
      lng: r.longitude,
      label: buildLocationLabel(r),
    };
    setLocation(loc);
    recordRecent(loc);
    setSearchResults([]);
    setSearch("");
  };

  const pickRecent = (loc) => {
    setLocation(loc);
    recordRecent(loc);
    setSearchResults([]);
    setSearch("");
  };

  // ---------- compute sun + moon ----------
  const data = useMemo(() => {
    if (!location) return null;
    const { lat, lng } = location;
    const times = SunCalc.getTimes(date, lat, lng);
    const moonTimes = SunCalc.getMoonTimes(date, lat, lng);
    const moonIllum = SunCalc.getMoonIllumination(date);

    const sunrisePos = SunCalc.getPosition(times.sunrise, lat, lng);
    const sunsetPos = SunCalc.getPosition(times.sunset, lat, lng);
    const noonPos = SunCalc.getPosition(times.solarNoon, lat, lng);

    const events = PHASE_ORDER.map((key) => ({
      key,
      time: times[key],
    })).filter((e) => e.time && !isNaN(e.time.getTime()));

    // sort chronologically (some events may roll into next day at high latitudes)
    events.sort((a, b) => a.time - b.time);

    const daylightMs =
      times.sunset && times.sunrise
        ? times.sunset.getTime() - times.sunrise.getTime()
        : 0;
    const daylightHours = daylightMs > 0 ? daylightMs / (1000 * 60 * 60) : 0;

    // Best shooting windows — only include windows whose start AND end times
    // are valid (skip at extreme latitudes during polar day/night).
    const validRange = (start, end) =>
      start && end && !isNaN(start.getTime()) && !isNaN(end.getTime()) && end > start;
    const bestWindows = [
      validRange(times.dawn, times.sunrise) && {
        id: "blue_morning",
        kind: "blue",
        start: times.dawn,
        end: times.sunrise,
      },
      validRange(times.sunrise, times.goldenHourEnd) && {
        id: "golden_morning",
        kind: "golden",
        start: times.sunrise,
        end: times.goldenHourEnd,
      },
      validRange(times.goldenHour, times.sunset) && {
        id: "golden_evening",
        kind: "golden",
        start: times.goldenHour,
        end: times.sunset,
      },
      validRange(times.sunset, times.dusk) && {
        id: "blue_evening",
        kind: "blue",
        start: times.sunset,
        end: times.dusk,
      },
    ].filter(Boolean);

    return {
      events,
      sunrise: { time: times.sunrise, az: compass(sunrisePos.azimuth) },
      sunset: { time: times.sunset, az: compass(sunsetPos.azimuth) },
      solarNoonAlt: (noonPos.altitude * 180) / Math.PI,
      daylightHours,
      bestWindows,
      moon: {
        rise: moonTimes.rise,
        set: moonTimes.set,
        phaseKey: moonPhaseKey(moonIllum.phase),
        illum: Math.round(moonIllum.fraction * 100),
      },
    };
  }, [location, date]);

  const shiftDate = (delta) => {
    const next = new Date(date);
    next.setDate(next.getDate() + delta);
    setDate(next);
  };

  const isToday = useMemo(() => {
    const today = new Date();
    return (
      today.getFullYear() === date.getFullYear() &&
      today.getMonth() === date.getMonth() &&
      today.getDate() === date.getDate()
    );
  }, [date]);

  return (
    <div
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      data-testid="golden-hour-page"
    >
      <div className="mb-6">
        <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2 flex items-center gap-3">
          <Sun weight="fill" className="w-8 h-8 text-[#F5A524]" />
          {c.title}
        </h1>
        <p className="text-[#A1A1AA] text-sm leading-relaxed max-w-2xl">
          {c.subtitle}
        </p>
      </div>

      {/* Location panel */}
      <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <MapPin weight="fill" className="w-4 h-4 text-[#F5A524]" />
          <h2 className="font-['Outfit'] text-white text-base font-medium">
            {c.location}
          </h2>
          {location && (
            <span
              className="ml-auto text-xs text-[#A1A1AA] truncate max-w-[60%]"
              data-testid="location-label"
              title={location.label}
            >
              {location.label}
            </span>
          )}
        </div>

        <div className="flex gap-1 mb-3 bg-[#0A0A0A] rounded-lg p-1 w-fit">
          {["auto", "manual"].map((id) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                tab === id
                  ? "bg-[#F5A524] text-[#0A0A0A]"
                  : "text-[#A1A1AA] hover:text-white"
              }`}
              data-testid={`location-tab-${id}`}
            >
              {id === "auto" ? c.auto : c.manual}
            </button>
          ))}
        </div>

        {tab === "auto" ? (
          <div>
            <Button
              onClick={handleAutoDetect}
              disabled={detectState === "loading"}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium"
              data-testid="auto-detect-btn"
            >
              <Crosshair className="w-4 h-4 mr-1.5" />
              {detectState === "loading" ? c.detect_loading : c.auto_detect}
            </Button>
            {detectState === "error" && (
              <p className="text-amber-400 text-xs mt-2 flex items-center gap-1">
                <WarningCircle weight="fill" className="w-3.5 h-3.5" />
                {detectError}
              </p>
            )}
          </div>
        ) : (
          <form onSubmit={handleSearch} className="space-y-2">
            {recents.length > 0 && (
              <div data-testid="recent-locations" className="mb-2">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#71717A] font-semibold mb-1.5">
                  <ClockCounterClockwise className="w-3 h-3" />
                  {c.recent}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {recents.map((r) => (
                    <div
                      key={r.label}
                      className={`group flex items-center bg-[#0A0A0A] border rounded-full pl-1 pr-1 py-1 transition-colors ${
                        r.pinned
                          ? "border-[#F5A524]/50"
                          : "border-white/10 hover:border-[#F5A524]/50"
                      }`}
                      data-testid={`recent-${r.label}`}
                    >
                      <button
                        type="button"
                        onClick={() => togglePin(r.label)}
                        className={`rounded-full p-1 transition-colors ${
                          r.pinned
                            ? "text-[#F5A524] hover:text-[#FCD34D]"
                            : "text-[#52525B] hover:text-[#F5A524] hover:bg-white/5"
                        }`}
                        aria-label={r.pinned ? c.unpin : c.pin}
                        aria-pressed={r.pinned}
                        title={r.pinned ? c.unpin : c.pin}
                        data-testid={`pin-${r.label}`}
                      >
                        <Star
                          weight={r.pinned ? "fill" : "regular"}
                          className="w-3 h-3"
                        />
                      </button>
                      {r.pinned && (
                        <button
                          type="button"
                          onClick={() => toggleDefault(r.label)}
                          className={`rounded-full p-1 transition-colors ${
                            r.isDefault
                              ? "text-[#F5A524] hover:text-[#FCD34D]"
                              : "text-[#52525B] hover:text-[#F5A524] hover:bg-white/5"
                          }`}
                          aria-label={r.isDefault ? c.unset_default : c.set_default}
                          aria-pressed={!!r.isDefault}
                          title={r.isDefault ? c.unset_default : c.set_default}
                          data-testid={`default-${r.label}`}
                        >
                          <House
                            weight={r.isDefault ? "fill" : "regular"}
                            className="w-3 h-3"
                          />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => pickRecent(r)}
                        className="text-xs text-[#D4D4D8] hover:text-white px-1.5 truncate max-w-[200px] flex items-center gap-1"
                        title={r.label}
                      >
                        <span className="truncate">{r.label}</span>
                        {r.isDefault && (
                          <span className="text-[9px] text-[#F5A524] font-semibold uppercase tracking-wider shrink-0">
                            · {c.is_default}
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeRecent(r.label)}
                        className="text-[#52525B] hover:text-amber-400 hover:bg-white/5 rounded-full p-1 transition-colors"
                        aria-label={c.remove_recent}
                        title={c.remove_recent}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={c.search_placeholder}
                className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-[#52525B] focus:ring-[#F5A524] focus:border-[#F5A524]"
                data-testid="city-search-input"
              />
              <Button
                type="submit"
                disabled={searching}
                className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium shrink-0"
                data-testid="city-search-btn"
              >
                {c.search_btn}
              </Button>
            </div>
            {searchResults.length > 0 && (
              <ul
                className="border border-white/10 rounded-lg overflow-hidden divide-y divide-white/5"
                data-testid="search-results"
              >
                {searchResults.map((r) => (
                  <li key={`${r.id}-${r.latitude}`}>
                    <button
                      type="button"
                      onClick={() => pickResult(r)}
                      className="w-full text-left px-3 py-2 text-sm text-[#D4D4D8] hover:bg-[#F5A524]/10 hover:text-white transition-colors flex items-baseline gap-2"
                      data-testid={`search-result-${r.id}`}
                    >
                      <span className="font-medium truncate">{r.name}</span>
                      <span
                        className={`text-xs shrink-0 ${
                          r.country_code === "US"
                            ? "text-[#F5A524] font-semibold"
                            : "text-[#71717A]"
                        }`}
                      >
                        {buildLocationSubtitle(r)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!searching && searchResults.length === 0 && search && (
              <p className="text-[#71717A] text-xs">{c.no_results}</p>
            )}
          </form>
        )}
      </div>

      {!location && (
        <div className="bg-[#121214]/60 border border-white/10 rounded-xl p-6 text-center">
          <p className="text-[#A1A1AA] text-sm">{c.no_location}</p>
        </div>
      )}

      {location && data && (
        <>
          {/* Date controls */}
          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => shiftDate(-1)}
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5 px-2"
                data-testid="date-prev"
                aria-label="Previous day"
              >
                <CaretLeft weight="bold" className="w-4 h-4" />
              </Button>
              <div className="px-3 py-1.5 bg-[#121214] border border-white/10 rounded-lg min-w-[180px] text-center">
                <p className="text-white text-sm font-medium" data-testid="date-label">
                  {fmtDateLong(date, lang)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => shiftDate(1)}
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5 px-2"
                data-testid="date-next"
                aria-label="Next day"
              >
                <CaretRight weight="bold" className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {!isToday && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDate(new Date())}
                  className="border-white/10 bg-transparent text-[#A1A1AA] hover:bg-white/5 hover:text-white"
                  data-testid="date-today"
                >
                  {c.today}
                </Button>
              )}
              <label
                className="flex items-center gap-2 px-3 py-1.5 bg-[#121214] border border-white/10 rounded-lg cursor-pointer text-[#A1A1AA] hover:text-white hover:border-white/20 text-sm transition-colors"
                data-testid="date-picker-label"
              >
                <CalendarBlank className="w-4 h-4" />
                <input
                  type="date"
                  value={fmtDateInput(date)}
                  onChange={(e) => {
                    const [y, m, d] = e.target.value.split("-").map(Number);
                    setDate(new Date(y, m - 1, d));
                  }}
                  className="bg-transparent border-none outline-none text-sm"
                  data-testid="date-picker-input"
                />
              </label>
            </div>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Stat
              label={c.sunrise}
              value={fmtTime(data.sunrise.time)}
              sub={`${data.sunrise.az.dir} (${data.sunrise.az.deg}°)`}
              color="text-[#FB923C]"
              testid="stat-sunrise"
            />
            <Stat
              label={c.sunset}
              value={fmtTime(data.sunset.time)}
              sub={`${data.sunset.az.dir} (${data.sunset.az.deg}°)`}
              color="text-[#EF4444]"
              testid="stat-sunset"
            />
            <Stat
              label={c.daylight}
              value={`${Math.floor(data.daylightHours)}h ${Math.round(
                (data.daylightHours % 1) * 60
              )}m`}
              sub=""
              color="text-[#F5A524]"
              testid="stat-daylight"
            />
            <Stat
              label={c.sun_alt_max}
              value={`${data.solarNoonAlt.toFixed(1)}°`}
              sub=""
              color="text-[#FCD34D]"
              testid="stat-altitude"
            />
          </div>

          {/* Best Shooting Windows */}
          {data.bestWindows.length > 0 && (
            <div
              className="bg-gradient-to-br from-[#F5A524]/10 via-[#121214]/80 to-[#3B82F6]/10 backdrop-blur-md border border-[#F5A524]/25 rounded-xl p-5 mb-6"
              data-testid="best-windows"
            >
              <h2 className="font-['Outfit'] text-white text-lg font-medium mb-1 flex items-center gap-2">
                <Sun weight="fill" className="w-5 h-5 text-[#F5A524]" />
                {c.best_windows}
              </h2>
              <p className="text-[#A1A1AA] text-xs leading-relaxed mb-4 max-w-2xl">
                {c.best_windows_sub}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {data.bestWindows.map((w) => {
                  const durationMin = Math.round((w.end - w.start) / 60_000);
                  const hh = Math.floor(durationMin / 60);
                  const mm = durationMin % 60;
                  const durLabel =
                    hh > 0 ? `${hh}h ${mm}m` : `${mm}m`;
                  const isGolden = w.kind === "golden";
                  const labelKey = `window_${w.id}`;
                  const tipKey = `${labelKey}_tip`;
                  return (
                    <div
                      key={w.id}
                      className={`relative rounded-xl p-4 border ${
                        isGolden
                          ? "bg-[#F5A524]/8 border-[#F5A524]/40"
                          : "bg-[#3B82F6]/8 border-[#3B82F6]/40"
                      }`}
                      data-testid={`window-${w.id}`}
                    >
                      <div
                        className={`absolute top-0 left-0 right-0 h-0.5 rounded-t-xl ${
                          isGolden ? "bg-[#F5A524]" : "bg-[#3B82F6]"
                        }`}
                        style={{
                          boxShadow: isGolden
                            ? "0 0 16px rgba(245, 165, 36, 0.6)"
                            : "0 0 16px rgba(59, 130, 246, 0.6)",
                        }}
                      />
                      <p
                        className={`text-[10px] uppercase tracking-[0.18em] font-semibold mb-1.5 ${
                          isGolden ? "text-[#F5A524]" : "text-[#60A5FA]"
                        }`}
                      >
                        {c[labelKey]}
                      </p>
                      <p className="font-['JetBrains_Mono'] text-white text-base font-semibold tabular-nums mb-1">
                        {fmtTime(w.start)} – {fmtTime(w.end)}{" "}
                        <span className="text-[#71717A] text-xs font-normal">
                          ({durLabel})
                        </span>
                      </p>
                      <p className="text-[#A1A1AA] text-xs leading-relaxed">
                        {c[tipKey]}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 mb-6"
            data-testid="sun-timeline"
          >
            <h2 className="font-['Outfit'] text-white text-lg font-medium mb-4">
              {c.timeline}
            </h2>
            <ol className="relative ml-4 border-l border-white/10 space-y-1">
              <AnimatePresence initial={false}>
                {data.events.map((e) => {
                  const phase = c.phases[e.key];
                  return (
                    <motion.li
                      key={e.key + e.time.getTime()}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.25 }}
                      className="pl-6 py-3 relative"
                      data-testid={`phase-${e.key}`}
                    >
                      <span
                        className="absolute -left-[7px] top-4 w-3.5 h-3.5 rounded-full border-2 border-[#0A0A0A] shadow-[0_0_0_2px_rgba(255,255,255,0.06)]"
                        style={{ backgroundColor: DOT_COLOR[e.key] }}
                      />
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div>
                          <p className="text-white font-medium text-sm">
                            {phase?.name || e.key}
                          </p>
                          <p className="text-[#71717A] text-xs">
                            {phase?.sub || ""}
                          </p>
                        </div>
                        <span className="font-['JetBrains_Mono'] text-[#F5A524] text-base font-semibold tabular-nums">
                          {fmtTime(e.time)}
                        </span>
                      </div>
                      {phase?.tip && (
                        <p className="text-[#A1A1AA] text-xs mt-1.5 leading-relaxed">
                          <span className="text-[#F5A524] font-semibold">
                            {c.tip}:{" "}
                          </span>
                          {phase.tip}
                        </p>
                      )}
                    </motion.li>
                  );
                })}
              </AnimatePresence>
            </ol>
          </div>

          {/* Moon */}
          <div
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5"
            data-testid="moon-info"
          >
            <h2 className="font-['Outfit'] text-white text-lg font-medium mb-4 flex items-center gap-2">
              <Moon weight="fill" className="w-5 h-5 text-[#A1A1AA]" />
              {c.moon_info}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat
                label={c.moonrise}
                value={fmtTime(data.moon.rise)}
                color="text-[#A1A1AA]"
                testid="stat-moonrise"
              />
              <Stat
                label={c.moonset}
                value={fmtTime(data.moon.set)}
                color="text-[#A1A1AA]"
                testid="stat-moonset"
              />
              <Stat
                label={c.phase}
                value={c.moon_phases[data.moon.phaseKey]}
                color="text-white"
                testid="stat-moon-phase"
              />
              <Stat
                label={c.illum}
                value={`${data.moon.illum}%`}
                color="text-[#FCD34D]"
                testid="stat-moon-illum"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub = "", color, testid }) {
  return (
    <div
      className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5"
      data-testid={testid}
    >
      <p className="text-[#71717A] text-[10px] uppercase tracking-widest font-semibold">
        {label}
      </p>
      <p className={`font-['JetBrains_Mono'] text-lg font-semibold tabular-nums ${color}`}>
        {value}
      </p>
      {sub && <p className="text-[#52525B] text-[10px] mt-0.5">{sub}</p>}
    </div>
  );
}
